import { observer } from 'mobx-react-lite'
import dayjs from 'dayjs'
import { generatePath, useHistory } from 'react-router-dom'
import { partition } from 'lodash'
import { useCallback } from 'react'

import Styles from './ActionResult.module.scss'

import { Section } from 'components/PostPreviewModalV2/components/Section/Section'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import { AccountPostStatus } from 'components/PostPreviewModalV2/components/AccountPostStatus/AccountPostStatus'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { useAlmanacFile } from 'hooks/queries/almanacFiles/useAlmanacFile'
import { FOLDER_PATH } from 'paths'
import { PostAction, usePostActionService } from 'components/PostPreviewModalV2/hooks/usePostActionService'
import { AutoScheduledButtons } from './AutoScheduledButtons/AutoScheduledButtons'
import { PostPreviewScreens, ScheduledFlowMode } from 'components/PostPreviewModalV2/PostPreviewStore'
import { Button } from 'blaze-ui/Button'
import { CalendarEdit } from 'blaze-ui/icons'
import { PostState } from 'components/PostPreviewModalV2/types'
import { usePostUnscheduleService } from 'components/PostPreviewModalV2/hooks/usePostUnscheduleService'
import { useModals } from 'almanac-ui/Modal'
import { pluralize } from 'utils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useRootStore } from 'stores/RootStore'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'

const formatDate = (date?: string | number | Date) => (date ? dayjs(date).format('ddd, MMM D, YYYY • h:mma') : '')

const getActionVerb = (action: PostAction): string => {
  switch (action) {
    case PostAction.PublishNow:
      return 'Publishing to'
    case PostAction.AutoSchedule:
      return 'Scheduling posts for'
    case PostAction.Reminder:
      return 'Creating reminders for'
    case PostAction.Draft:
      return 'Saving drafts for'
    case PostAction.Unschedule:
      return 'Unscheduling posts for'
    default:
      return 'Working on'
  }
}

const getPublishingSectionTitle = (action: PostAction): string => {
  switch (action) {
    case PostAction.PublishNow:
      return 'Publishing to'
    case PostAction.AutoSchedule:
      return 'Scheduling for'
    case PostAction.Reminder:
      return 'Reminder set for'
    default:
      return 'Working on'
  }
}

export const ActionResult = observer(() => {
  const store = usePostPreviewStore()
  const history = useHistory()
  const { closeAllModals } = useModals()
  const { unschedule } = usePostUnscheduleService()
  const { run } = usePostActionService()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()

  const publishing = store.postStates.filter((s) => ['creating', 'publishing'].includes(s.runtime))
  const successes = store.postStates.filter((s) => s.runtime === 'success')
  const failures = store.postStates.filter((s) => s.runtime === 'failure')

  const [published, rest1] = partition(successes, (s) => s.result === 'published')
  const [autoScheduled, rest2] = partition(rest1, (s) => s.result === 'autoScheduled')
  const [reminders, drafts] = partition(rest2, (s) => s.result === 'reminderScheduled')
  const { fileId } = usePostPreviewContext()

  const doNow = useCallback(() => run(PostAction.PublishNow), [run])

  const isThisFile = useCallback(
    (postState: PostState) => {
      if (store.flow === ScheduledFlowMode.CROSSPOST || store.flow === ScheduledFlowMode.WIZARD) return false

      return postState.fileId === fileId
    },
    [fileId, store.flow],
  )

  const firstFile = useAlmanacFile(store.postStates[0]?.fileId)
  const projectSeo = firstFile.data?.almanac_file?.parent?.seo_string
  const projectPath = projectSeo ? generatePath(FOLDER_PATH, { folderSlug: projectSeo }) : undefined

  const handleOpenProject = () => {
    if (projectPath) {
      history.push(projectPath)
      handleClose()
    }
  }

  const handleClose = () => {
    if (isMobile) {
      drawersStore.closeAllDrawers()
    } else {
      closeAllModals()
    }
  }

  const heading = (() => {
    if (publishing.length) {
      switch (store.lastAction) {
        case PostAction.PublishNow:
          return 'Publishing your posts…'
        case PostAction.AutoSchedule:
          return 'Scheduling your posts…'
        case PostAction.Reminder:
          return 'Setting your reminders…'
        default:
          return 'Working…'
      }
    }
    if (failures.length) return 'Finished – some posts failed'
    if (published.length) return 'Success! Your content is live'
    if (autoScheduled.length || reminders.length) return 'Your posts are ready to go!'
    return 'Your drafts have been saved!'
  })()

  const subHeading = (() => {
    if (publishing.length) {
      return `${getActionVerb(store.lastAction || PostAction.PublishNow)} ${pluralize('account', publishing.length)}`
    }

    if (failures.length) {
      return `${successes.length} succeeded • ${failures.length} failed`
    }

    if (published.length && drafts.length) {
      return `${published.length} published • ${drafts.length} saved`
    }

    if (autoScheduled.length || reminders.length) {
      return 'You can still change the scheduled date, time, or channels.'
    }

    return ''
  })()

  const scheduledDate = autoScheduled[0]?.postAt || reminders[0]?.postAt || store.postAt
  const existingDate =
    autoScheduled[0]?.postAt ||
    reminders[0]?.postAt ||
    published[0]?.postAt ||
    drafts[0]?.postAt ||
    failures[0]?.postAt ||
    store.postAt

  const shouldShowAutoButtons = true

  const autoButtons = shouldShowAutoButtons ? (
    <AutoScheduledButtons
      isExisting={store.flow === 'edit'}
      onCrossPost={store.flow === 'edit' ? store.startCrossPost : undefined}
      onOpenProject={handleOpenProject}
      onPostNow={doNow}
      onUnschedule={unschedule}
      onClose={handleClose}
    />
  ) : null

  const renderSection = (
    title: string,
    list: PostState[],
    extraActions?: React.ReactNode,
    customContent?: React.ReactNode,
  ) =>
    list.length > 0 || customContent ? (
      <Section title={title} actions={extraActions}>
        {customContent ??
          list.map((ps) => (
            <AccountPostStatus
              key={ps.fileId}
              state={ps}
              isDesign={store.target === 'design'}
              isThisFile={isThisFile(ps)}
            />
          ))}
      </Section>
    ) : null

  return (
    <div className={Styles.root}>
      {store.flow !== 'edit' && (
        <header className={Styles.header}>
          <Heading level={4}>{heading}</Heading>
          {subHeading && (
            <Text variant="placeholder" color="var(--dark-80)">
              {subHeading}
            </Text>
          )}
        </header>
      )}

      {store.flow === 'edit' &&
        renderSection(
          'Scheduled date',
          [],
          <Button
            variant="ghost"
            size="sm"
            frontIcon={CalendarEdit}
            onPress={() => store.setScreen(PostPreviewScreens.AUTOPOST_CALENDAR_SCREEN)}
          >
            Change
          </Button>,
          formatDate(existingDate),
        )}

      {renderSection(getPublishingSectionTitle(store.lastAction || PostAction.PublishNow), publishing)}

      {store.flow !== 'edit' &&
        (autoScheduled.length > 0 || reminders.length > 0) &&
        renderSection('Scheduled date', [], undefined, formatDate(scheduledDate))}

      {renderSection('Published to', published)}
      {renderSection('Publishing to', autoScheduled)}
      {renderSection('Reminder set for', reminders)}
      {renderSection('Drafts saved', drafts)}
      {renderSection('Failed', failures)}

      {autoButtons}
    </div>
  )
})
